import AssetManager from 'AssetManager';
import { fromJS, is, Map, merge } from 'immutable';
import { DefaultDataGenerator } from '@pearsontechnology/aero-plugin-schema';
import { isImmutable } from 'pluginServices/helpers/immutableHelpers';
import { LEARNER, EDITOR } from 'pluginServices/constants/context';
import ComponentResolver from 'resolvers/RTMResolvers/ComponentResolver';
import { createVersionExpression } from 'utils/versionExpressions';
import { composeState } from 'utils/scope/composeScope';
import { compareObject } from 'utils/comparison';
import PreviewService from 'services/PreviewService';
import scopeBindingManager from 'ScopeBindingManager';
import isObject from 'lodash/isObject';

import PluginService from '../PluginService';
import InteractiveService from '../InteractiveService';
import ActivityService from '../ActivityService';
import UndoRedoService from 'services/UndoRedoService';
import { getAction } from 'utils/undoredo';
import { ELEMENT_CONFIG_TYPES, EVENT_NAMES } from 'services/UndoRedoService/constants/undoRedo';
import { editablePlugins } from './constants';

const CONFIG_KEY_PATH = ['config'];
const CONFIG_CHANGE = 'CONFIG_CHANGE';

/**
 * A service that provides access to courseware components
 */
export default (() => {
  /** @type {ComponentResolver} */
  let componentResolver;
  let isRegistered = false;

  let _componentCache = Map();
  const _subscriptions = {};

  function _createUnsubscribe(componentId, listener) {
    return () => {
      const listenerIndex = _subscriptions[componentId].indexOf(listener);
      _subscriptions[componentId].splice(listenerIndex, 1);
      if (_subscriptions[componentId].length === 0) {
        _forgetComponent(componentId);
      }
    };
  }

  function _notifyListeners(componentId, context) {
    if (_subscriptions[componentId]) {
      const listeners = _subscriptions[componentId];
      const updatedComponentObject = _componentCache.get(componentId).toJS();
      listeners.forEach((listener) => {
        listener(updatedComponentObject, context);
      });
    }
  }

  function _forgetComponent(componentId) {
    _componentCache = _componentCache.remove(componentId);
  }

  function updateAssetManager(component) {
    const assets = component.assets;
    if (assets) {
      AssetManager.addResolvedToCache(assets);
    }
  }

  function _parseAndSaveToCache(component) {
    const jsComponent = component;
    if (typeof jsComponent.config === 'string') {
      try {
        jsComponent.config = JSON.parse(jsComponent.config);
      } catch (e) {
        throw new Error(`Component ${jsComponent.componentId} config parse error: ${jsComponent.config}`);
      }
    }
    _componentCache = _componentCache.set(jsComponent.componentId, fromJS(jsComponent));
    updateAssetManager(jsComponent);
    _notifyListeners(jsComponent.componentId);
  }

  function _dangerouslySetConfigInCache(componentId, newConfig) {
    const jsConfig = isImmutable(newConfig) ? newConfig.toJS() : newConfig;
    const immutableConfig = fromJS(jsConfig);
    // save and notify only if the component config is different to what we already have
    const oldConfig = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH]);
    if (!is(oldConfig, immutableConfig)) {
      _componentCache = _componentCache.setIn([componentId, ...CONFIG_KEY_PATH], immutableConfig);
      _notifyListeners(componentId);
    }
  }

  const setInPreviewScope = async (componentId, newScope) => {
    const config = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH]);
    const { pluginId, version } = _componentCache.getIn([componentId, 'plugin']).toJS();
    const schema = await getPluginSchema(pluginId, version);
    const previewState = composeState(schema, config, newScope);
    PreviewService.setPreviewScope([componentId], previewState);
    if (!is(config, fromJS(previewState))) {
      _notifyListeners(componentId, LEARNER);
    }
  };

  /**
   * Creates a new component in an interactive.
   *   see [RTM Doc](https://dev-developers.smartsparrow.com/messages/author-interactive-component-create/)
   * @param {string} interactiveId
   * @param {string} pluginId
   * @param {string} pluginVersion
   * @param {object} config
   * @returns {string} Component's componentId
   */
  const createInteractiveComponent = async (interactiveId, pluginId, pluginVersion, config) => {
    const generator = new DefaultDataGenerator();
    const schema = await getPluginSchema(pluginId, pluginVersion);
    const defaultConfig = generator.generateDefaultData(schema);
    const expressedPluginVersion = createVersionExpression(pluginVersion);
    const currentConfig = config || defaultConfig;
    currentConfig.__metadata = { pluginVersion };

    const component = await componentResolver.authorInteractiveComponentCreate(
      interactiveId,
      pluginId,
      expressedPluginVersion,
      JSON.stringify(currentConfig),
    );
    const componentWithPluginVersion = { ...component, plugin: { ...component.plugin, latestVersion: pluginVersion } };
    _parseAndSaveToCache(componentWithPluginVersion);
    await InteractiveService.setComponentInInteractive(interactiveId, component.componentId);
    return component.componentId;
  };

  /**
   * Creates a new component in an interactive.
   *   see [RTM Doc](https://dev-developers.smartsparrow.com/messages/author-activity-component-create/)
   * @param {string} interactiveId
   * @param {string} pluginId
   * @param {string} pluginVersion
   * @returns {string} Component's componentId
   */
  const createActivityComponent = async (activityId, pluginId, pluginVersion) => {
    const generator = new DefaultDataGenerator();
    const schema = await getPluginSchema(pluginId, pluginVersion);
    const config = generator.generateDefaultData(schema);
    config.__metadata = { pluginVersion };
    const expressedPluginVersion = createVersionExpression(pluginVersion);
    const component = await componentResolver.authorActivityComponentCreate(
      activityId,
      pluginId,
      expressedPluginVersion,
      JSON.stringify(config),
    );
    await ActivityService.setComponentInActivity(activityId, component.componentId);
    return {
      componentId: component.componentId,
      componentName: component.plugin.name,
    };
  };

  /**
   * Fetches the Plugin Schema, given a pluginId and version
   * @todo Remove this.  Client code should use the PluginService directly
   * @param {string} pluginId
   * @param {string} pluginVersion
   */
  const getPluginSchema = async (pluginId, pluginVersion) => {
    const response = await PluginService.getPlugin(pluginId, pluginVersion);
    return JSON.parse(response.manifest.configurationSchema);
  };

  /**
   * Fetches the Plugin Schema, given a componentId
   * @param {string} componentId
   */
  async function getSchemaForComponent(componentId) {
    const { plugin: { pluginId, version } } = await getComponent(componentId);
    const schema = await getPluginSchema(pluginId, version);
    // return format to match Activity and Interative services
    return [
      {
        schema,
      },
    ];
  }

  /**
   * Removes a component from an interactive.
   * @param {string} componentId
   * @param {string} parentInteractiveId
   */
  const deleteInteractiveComponent = async (componentId, parentInteractiveId) => {
    await componentResolver.authorInteractiveComponentDelete(
      componentId,
      parentInteractiveId,
    );
  };

  /**
   * Removes multiple components from an interactive.
   * @param {[string]} componentIds
   * @param {string} interactiveId
   */
  const deleteInteractiveComponents = async (componentIds, interactiveId) => {
    const response = await componentResolver.authorInteractiveComponentsDelete(
      componentIds,
      interactiveId,
    );
    return response;
  };

  /**
   * Restores components for an interactive.
   * @param {string} interactiveId
   * @param {[string]} componentIds
   */
  const restoreInteractiveComponent = async (interactiveId, componentIds) => {
    const response = await componentResolver.authorInteractiveComponentRestore(
      interactiveId,
      componentIds,
    );
    return response.components;
  };

  /**
   * Removes a component from an activity.
   * @param {string} componentId
   * @param {string} parentActivityId
   */
  const deleteActivityComponent = async (componentId, parentActivityId) => {
    await componentResolver.authorActivityComponentDelete(
      componentId,
      parentActivityId,
    );
  };

  /**
   * Given a component Id, check if it exists in the local cache.
   * @param {string} id
   * @returns {boolean} The existence of the component in cache.
   */
  const has = (id) => (
    _componentCache.has(id));

  /**
   * Retrieves a component, either from the cache, or from the RTM.
   * @param {string} componentId
   * @returns {ComponentDTO}
   */
  const getComponent = async (componentId) => {
    if (!componentId) {
      return null;
    }

    if (!has(componentId)) {
      const component = await componentResolver.authorComponentGet(
        componentId,
      );
      _parseAndSaveToCache(component);
    }

    const component = _componentCache.get(componentId).toJS();
    scopeBindingManager.saveBoundReferences(component.componentId, component.config);
    return component;
  };

  const isComponent = (id) => has(id);

  /**
   * Fetches a component configuration subsection from a path.
   * @param {string} componentId
   * @param {[string]=} keyPath
   */
  const getInComponent = async (componentId, keyPath = []) => {
    await getComponent(componentId);
    const selectedData = _componentCache.getIn([componentId, ...keyPath]);

    if (selectedData === null || typeof selectedData === 'undefined') {
      return selectedData;
    }
    return isImmutable(selectedData) ? selectedData.toJS() : selectedData;
  };

  /**
   * Fetches a component configuration subsection from a path, starting at
   * the config section.
   * @param {string} componentId
   * @param {[string]=} keyPath
   * @param {string} context
   */
  const getInComponentConfig = async (componentId, keyPath = [], context = EDITOR) => (
    // - Check if this is Editor flow or Learner(preview).
    // - Preview context will be set as Learner
    // - Preview flow fetch previewScope
    context === EDITOR ? getInComponent(componentId, [...CONFIG_KEY_PATH, ...keyPath]) : PreviewService.getInPreviewScope([componentId])
  );

  /**
   * Replaces the component's configuration at the specified keypath with
   * newConfigPart.
   * @param {string} componentId
   * @param {string} keyPath
   * @param {Object} newConfigPart
   */
  async function saveInComponentConfig(componentId, keyPath, newConfigPart = null, eventSource = EVENT_NAMES.COMPONENTS_STAGE) {
    if (!_componentCache.has(componentId)) {
      return null;
    }

    const oldConfigPart = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH, ...keyPath]);
    if (compareObject(oldConfigPart, newConfigPart)) {
      return componentId;
    }

    const oldConfig = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH]) || fromJS({});

    const newConfig = keyPath.length ? oldConfig.setIn(keyPath, newConfigPart) : merge(oldConfig, newConfigPart);

    let updatedConfig = newConfig;

    if (eventSource === EVENT_NAMES.COMPONENTS_STAGE) {
      const { plugin: { name } } = await getComponent(componentId);
      const elementType = `${ELEMENT_CONFIG_TYPES.COMPONENT}_${componentId}`;
      const isInitalConfig = await UndoRedoService.loadInitialConfig(elementType, componentId, updatedConfig.toJS());

      const isEditablePlugin = editablePlugins.includes(name);
      if (isEditablePlugin && !isInitalConfig) {
        const recordConfig = isObject(newConfigPart) ? newConfigPart : updatedConfig.toJS();
        const action = getAction(EVENT_NAMES.COMPONENTS_STAGE, recordConfig);
        action.elementType = elementType;
        UndoRedoService.recordAction(action, recordConfig, componentId);
      }

    }
    // This is how we can get an invalid cache (not waiting for backend response)
    _dangerouslySetConfigInCache(componentId, newConfig);

    /*We want workspace to add metadata property capturing the version of the plugin to the config
    that is passed in from the plugin before it sends it to Mercury: This is needed in order to know
    which logic we need to apply when converting the state object later on (e.g when indexing for csg).
    */
    updatedConfig = { ...updatedConfig.toJS(), __metadata: { pluginVersion: _componentCache.get(componentId).toJS().plugin.latestVersion } };

    /* ignore own broadcast */
    if (JSON.stringify(oldConfig.toJS()) !== JSON.stringify(updatedConfig)) {
      await componentResolver.authorComponentConfigReplace(componentId, JSON.stringify(updatedConfig));
    }
    scopeBindingManager.saveBoundReferences(componentId, updatedConfig);
    return componentId;
  }

  function saveInComponentPreviewScope(componentId, keyPath, newConfigPart) {
    const oldConfigPart = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH, ...keyPath]);
    if (compareObject(oldConfigPart, newConfigPart)) {
      return componentId;
    }
    const oldConfig = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH]) || fromJS({});
    const newScope = oldConfig.setIn(keyPath, newConfigPart);
    // This is how we can get an invalid cache (not waiting for backend response)
    setInPreviewScope(componentId, newScope);
    return componentId;
  }

  /**
   * Subscribes to component changes.  Note, this will also trigger the callback
   *   on subscribing, in addition to subsequent changes to the component.
   * @param {string} componentId
   * @param {function(*)} listener
   */
  function subscribeToComponent(componentId, listener) {
    _subscriptions[componentId] = _subscriptions[componentId] || [];
    _subscriptions[componentId].push(listener);

    if (has(componentId)) {
      listener(_componentCache.get(componentId).toJS());
    } else {
      getComponent(componentId, listener);
    }

    return _createUnsubscribe(componentId, listener);
  }

  /**
   * Subscribes to an optional path within the component's configuration.  The
   *   initial subscription will also trigger the listener, in addition to
   *   subsequent changes to the subscribed config.
   * @param {string} componentId
   * @param {string} keyPath
   * @param {function(*)} listener
   */
  function subscribeToComponentConfig(componentId, keyPath = [], listener) {
    const componentListener = async (updatedConfig, context) => {
      const interactivePart = await getInComponentConfig(componentId, keyPath, context);
      listener(interactivePart);
    };
    return subscribeToComponent(componentId, componentListener);
  }

  /**
   * A listener that handles a broadcast message and maintains component updates.
   * @param {Object} broadcast
   */
  function receiveComponentChange(broadcast) {
    const componentId = broadcast.response.elementId;
    if (!_componentCache.has(componentId)) {
      return;
    }
    const component = _componentCache.get(componentId).toJS();

    switch (broadcast.response.action) {
      case CONFIG_CHANGE:
        /* only update component config and broadcast if data changed */
        if (!compareObject(component.config, broadcast.response.config)) {
          component.config = broadcast.response.config;
          _parseAndSaveToCache(component);
        }
        break;
      default:
        // The other types might not apply to components, implement as needed
        break;
    }
  }

  /**
 * This function is called while deleting value binded properties which are not part of data object.
 * @param  componentId
 * @param  newConfig
 * @returns  componentId
 */
  async function deleteValueBindedReference(componentId, newConfig) {
    if (!_componentCache.has(componentId)) {
      return null;
    }

    const oldConfigPart = _componentCache.getIn([componentId, ...CONFIG_KEY_PATH]);
    if (compareObject(oldConfigPart, newConfig)) {
      return componentId;
    }

    // This is how we can get an invalid cache (not waiting for backend response)
    _dangerouslySetConfigInCache(componentId, newConfig);
    // Send the new config to author.component.replace call
    await componentResolver.authorComponentConfigReplace(componentId, JSON.stringify(newConfig));

    return componentId;
  }

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      componentResolver = new ComponentResolver(connectionProvider);
    },

    createInteractiveComponent,
    deleteInteractiveComponent,
    deleteInteractiveComponents,
    restoreInteractiveComponent,
    createActivityComponent,
    deleteActivityComponent,

    has,
    isComponent,
    getComponent,
    getInComponent,
    getInComponentConfig,

    saveInComponentConfig,
    saveInComponentPreviewScope,
    setInPreviewScope,
    subscribeToComponent,
    subscribeToComponentConfig,
    receiveComponentChange,
    getPluginSchema,
    getSchemaForComponent,
    deleteValueBindedReference,
  };
})();
